import React from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Carousel,
  ResponsiveContext,
  Text,
} from 'grommet';
import { Subheading } from '@/components/Subheading';
import { Checkmark } from 'grommet-icons';

const Title = ({ children, ...rest }) => (
  <Text alignSelf="start" textAlign="start" size="xlarge" {...rest}>
    {children}
  </Text>
);

const PlanHeader = ({
  title,
  price,
  billed,
  originalPrice = undefined,
  titleProps = {},
  active = false,
  ...rest
}) => (
  <CardHeader
    direction="column"
    gap="none"
    pad="large"
    background="background-front"
    {...rest}
  >
    <Title {...titleProps}>{title.toUpperCase()}</Title>
    <Box fill="horizontal" margin={{ top: `medium` }}>
      <Text
        color="status-error"
        weight={800}
        style={{ textDecoration: originalPrice ? `line-through` : `none` }}
      >
        {originalPrice ? `$${originalPrice.toFixed(2)}` : `FREE`}
      </Text>
      <Box direction="row" align="end" margin={{ top: `xsmall` }}>
        <Text weight={600} margin={{ vertical: `xsmall` }} alignSelf="start">
          $
        </Text>
        <>
          <Text weight={600} size="2xl" margin={{ left: `xxsmall` }}>
            {price.toFixed(2)}
          </Text>
          <Text size="small"> /month</Text>
        </>
      </Box>
      <Text
        margin={{ top: `medium` }}
        size="small"
        color="light-4"
        style={{ textTransform: `uppercase` }}
      >
        {billed}
      </Text>
      <Button
        style={{ textAlign: `center` }}
        margin={{ top: `medium` }}
        primary={active}
        secondary={!active}
        href="#cta"
        color="brand"
        label="Start Now"
        fill="horizontal"
        alignSelf="center"
      />
    </Box>
  </CardHeader>
);

const Service = ({ children }) => (
  <Box direction="row" pad="xxsmall" margin="small" align="center" flex={false}>
    <Checkmark color="brand" size="small" />
    <Text margin={{ horizontal: `small` }} size="small">
      {children}
    </Text>
  </Box>
);

const PlanBody = ({ includedServices, ...rest }) => (
  <CardBody background="background-contrast" pad="medium" {...rest}>
    {includedServices.map((includedService) => (
      <Service key={includedService}>{includedService}</Service>
    ))}
  </CardBody>
);

const PlanCard = ({ children, ...rest }) => (
  <Card
    margin="small"
    background="background-front"
    elevation="xlarge"
    {...rest}
  >
    {children}
  </Card>
);

const planCards = [
  {
    animation: { type: `slideLeft`, delay: 800 },
    title: `Personal`,
    price: 19,
    billed: ``,
    originalPrice: 49,
    includedServices: [
      `Automated Data Removals`,
      `Monitor Your Digital Footprint`,
      `AI Powered Automation`,
      `DarkWeb Monitoring of 140+ Billion Records`,
      `9,000+ Security Tests`,
      `Dedicated Support`,
    ],
  },
  {
    animation: { type: `slideLeft`, delay: 600 },
    active: true,
    originalPrice: 179,
    title: `Small Business`,
    titleWeight: 800,
    price: 49,
    billed: ``,
    includedServices: [
      `24/7 Website Testing Powered by AI and Red Team Experts`,
      `Automated Data Removals for Employees and Executives`,
      `AI Powered Automation`,
      `DarkWeb Monitoring of 140+ Billion Records`,
      `22,000+ Security Tests`,
      `US Based Support and Dedicated Account Executive`,
    ],
  },
  {
    animation: { type: `slideLeft`, delay: 400 },
    originalPrice: 1499,
    title: `Pentester Elite`,
    titleWeight: 600,
    price: 899,
    billed: ``,
    includedServices: [
      `24/7 Website Testing Powered by AI and Red Team Experts`,
      `Automated Data Removals for Employees and Executives`,
      `AI Powered Automation`,
      `DarkWeb Monitoring of 140+ Billion Records`,
      `39,000+ Security Tests`,
      `US Based Support and Dedicated Account Executive`,
      `Annual Penetration Test & Monthly Manual Testing`,
    ],
  },
];

export const Pricing = () => (
  <Box align="center">
    <Subheading>FREE TO TRY. EASY TO USE. RISK FREE.</Subheading>
    <Text size="3xl" weight={600} margin="medium" textAlign="center">
      Pricing Based on Needs
    </Text>
    <ResponsiveContext.Consumer>
      {(size) =>
        size === `small` ? (
          <Carousel fill controls="arrows">
            {planCards.map((planCard) => (
              <Box align="center" key={planCard.title} pad="large">
                <PlanCard
                  width="medium"
                  border={planCard.active && { color: `brand`, size: `medium` }}
                  animation={planCard.animation}
                  height="750px"
                >
                  <PlanHeader
                    active={planCard.active}
                    originalPrice={planCard.originalPrice}
                    title={planCard.title}
                    titleProps={planCard.titleWeight}
                    price={planCard.price}
                    billed={planCard.billed}
                  />
                  <PlanBody
                    includedServices={planCard.includedServices}
                    overflow="auto"
                  />
                </PlanCard>
              </Box>
            ))}
          </Carousel>
        ) : (
          <Box direction="row" justify="center" wrap>
            {planCards.map((planCard) => (
              <PlanCard
                key={planCard.title}
                width="medium"
                border={planCard.active && { color: `brand`, size: `medium` }}
                animation={planCard.animation}
              >
                <PlanHeader
                  active={planCard.active}
                  originalPrice={planCard.originalPrice}
                  title={planCard.title}
                  titleProps={planCard.titleWeight}
                  price={planCard.price}
                  billed={planCard.billed}
                />
                <PlanBody includedServices={planCard.includedServices} />
              </PlanCard>
            ))}
          </Box>
        )
      }
    </ResponsiveContext.Consumer>
  </Box>
);
